import { type APP_LANGUAGE, APP_LANGUAGES, DEFAULT_LOCALE } from "@/utils/i18n/t11n"
import { isBlank, isNotBlank, isString } from "@/utils/ts-utils"
import { type Dispatch, type SetStateAction, createContext, useContext, useState } from "react"
import { useParams } from "react-router-dom"

interface AppLanguage {
  setLocale: Dispatch<SetStateAction<string>>
  getLocale: () => string
  getLang: () => string
}

const appLanguageContext = createContext<AppLanguage>({
  setLocale: () => null,
  getLocale: () => "",
  getLang: () => "",
})

const saveLocale = (locale: string) => {
  const lang = locale.substring(0, 2)
  window?.localStorage?.setItem(
    "appLanguage",
    APP_LANGUAGES.includes(lang as APP_LANGUAGE) ? lang : DEFAULT_LOCALE.substring(0, 2)
  )
}

const detectDefaultLocale = () => {
  let saveToConf = false

  // 1. URL param forced
  let locale = useParams().locale ?? ""
  if (isNotBlank(locale)) {
    saveToConf = true
  }

  // 2. Conf detection
  if (isBlank(locale)) {
    locale = window?.localStorage?.getItem("appLanguage") ?? ""
  }

  // 3. Browser
  if (isBlank(locale)) {
    for (const lang of navigator?.languages ?? []) {
      const lg = lang.substring(0, 2).toLocaleLowerCase()

      if (APP_LANGUAGES.includes(lg as APP_LANGUAGE)) {
        locale = lg
        saveToConf = true
        break
      }
    }
  }

  // 4. Default
  if (isBlank(locale)) {
    locale = DEFAULT_LOCALE
    saveToConf = true
  }

  if (saveToConf) saveLocale(locale)

  return locale
}

export const AppLanguageContext = (props: React.PropsWithChildren) => {
  const defaultLocale = detectDefaultLocale()
  const [locale, _setLocale] = useState<string>(defaultLocale)

  const getLocale = () => locale
  const getLang = () => getLocale().substring(0, 2)
  const setLocale: Dispatch<SetStateAction<string>> = (
    value: string | ((prevState: string) => string)
  ) => {
    let v = isString(value) ? value : value(getLocale())
    if (!APP_LANGUAGES.includes(v.substring(0, 2) as APP_LANGUAGE)) v = DEFAULT_LOCALE
    saveLocale(v)
    return _setLocale(v)
  }

  return (
    <appLanguageContext.Provider value={{ setLocale, getLocale, getLang }}>
      {props.children}
    </appLanguageContext.Provider>
  )
}

export const useAppLanguageContext = () => useContext(appLanguageContext)
